import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { renderTooltipIcon } from 'components/TableauObs/common/KpiCard';

export interface PieChartItem {
    name: string;
    value: number;
    count?: number;
}

interface PieChartWithLegendWidgetProps {
    data: PieChartItem[];
    colorMapping: { [key: string]: string };
    isLoading?: boolean;
    title?: string;
    hasTooltip?: boolean;
    tooltipText?: string;
    theme?: 'light' | 'dark';
    containerProps?: { sx?: object };
}

const PieChartWithLegendWidget: React.FC<PieChartWithLegendWidgetProps> = ({
    data = [],
    colorMapping = {},
    isLoading = false,
    title,
    hasTooltip = false,
    tooltipText,
    containerProps = {},
    theme = 'light'
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        function onWindowResize() {
            setViewportWidth(window.innerWidth);
        }
        window.addEventListener('resize', onWindowResize);
        return () => {
            window.removeEventListener('resize', onWindowResize);
        };
    }, []);

    let total = 0;
    for (const item of data) {
        total += item.value || 0;
    }

    const seriesData = data.map((item) => {
        return {
            name: item.name,
            value: item.value,
            count: item.count,
            itemStyle: {
                color: colorMapping[item.name] || '#4A90E2',
            },
        };
    });

    const chartOption = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            borderColor: '#ccc',
            borderWidth: 1,
            formatter: (params: any) => {
                const count = params.data?.count ?? params.value;
                const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0.0';
                return `${params.name}: ${count} (${pct}%)`;
            },
        },
        legend: {
            show: false,
        },
        series: [
            {
                type: 'pie',
                radius: ['0%', '90%'],
                center: ['50%', '50%'],
                avoidLabelOverlap: false,
                label: {
                    show: false,
                    position: 'inside',
                    formatter: (params: any) => {
                        const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0';
                        return `${pct}%`;
                    },
                    fontSize: 14,
                    fontWeight: 'bold',
                    color: '#fff',
                    fontFamily: 'Montserrat, sans-serif',
                },
                labelLine: { show: false },
                emphasis: {
                    scale: true,
                    scaleSize: 6,
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (params: any) => {
                            const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0';
                            return `${pct}%`;
                        },
                        fontSize: 14,
                        fontWeight: 'bold',
                        color: '#fff',
                        fontFamily: 'Montserrat, sans-serif',
                    },
                },
                data: seriesData,
            },
        ],
    };

    const emptyDatasetConfig = React.useMemo(() => ({
        backgroundColor: 'transparent',
        title: {
            text: 'No data',
            show: true,
            left: 'center',
            top: 'center',
            textStyle: {
                fontWeight: 'normal',
                color: '#aaa',
                fontSize: 14,
            },
        },
        series: [],
    }), []);

    const widgetContainerStyles = {
        backgroundColor: '#ffffff',
        minHeight: '318px',
        display: 'flex',
        flexDirection: 'column' as const,
        borderRadius: '5px',
        p: 2.5,
        ...containerProps?.sx,
    };

    return (
        <Box sx={widgetContainerStyles}>
            {/* Header Section */}
            <Stack alignItems="center" justifyContent="space-between" direction="row" mb={1}>
                <Typography
                    sx={{ color: '#1a1a2e', fontSize: 20, fontWeight: 500 }}
                >
                    {title}
                </Typography>
                {hasTooltip && renderTooltipIcon(tooltipText || "", theme)}
            </Stack>

            {/* Visualization Area */}
            <Box
                key={viewportWidth}
                ref={chartContainerRef}
                sx={{
                    flex: 1,
                    minHeight: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                {isLoading && <LoaderComponent />}
                {!isLoading && (
                    <Box sx={{ display: 'flex', height: '100%', width: '100%', alignItems: 'center' }}>
                        <Box sx={{ height: '220px', width: '55%', position: 'relative' }}>
                            <Box
                                sx={{
                                    width: '120px',
                                    background: 'rgba(0,0,0,0.15)',
                                    height: '30px',
                                    left: '50%',
                                    bottom: '-20px',
                                    position: 'absolute',
                                    filter: 'blur(8px)',
                                    transform: 'translateX(-50%)',
                                    borderRadius: '50%',
                                    zIndex: 0,
                                }}
                            />
                            <ReactECharts
                                opts={{ renderer: 'canvas' }}
                                style={{ height: '100%', width: '100%', position: 'relative', zIndex: 1 }}
                                option={data.length ? chartOption : emptyDatasetConfig}
                                lazyUpdate
                                notMerge
                            />
                        </Box>
                        {/* Legend Container */}
                        <Box
                            sx={{
                                gap: 1.2,
                                display: 'flex',
                                flexDirection: 'column',
                                ml: 1,
                                flex: 1,
                            }}
                        >
                            {data.map((item, index) => {
                                return (
                                    <Stack key={item.name} direction="row" alignItems="center" gap={1}>
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 10,
                                                borderRadius: '2px',
                                                backgroundColor: colorMapping[item.name] || '#4A90E2',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily: 'Montserrat, sans-serif',
                                                fontSize: '12px',
                                                color: '#444',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.name}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PieChartWithLegendWidget;
